import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Counter } from '../types/entities';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

interface CounterState {
  counters: Counter[];
  counter: Counter | null;
  loading: boolean;
  error: string | null;
}

const initialState: CounterState = {
  counters: [],
  counter: null,
  loading: false,
  error: null,
};


export const fetchCounters = createAsyncThunk<Counter[], void, { state: RootState }>(
  'counter/fetchCounters',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
   //   const response = await axios.get(`${baseUrlApi}/counterstatments/all/${authentification.user.OwnerId}`, {
      const response = await axios.get(`${baseUrlApi}/counterstatments`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch counters');
    }
  }
);

export const fetchCounter = createAsyncThunk<Counter, number, { state: RootState }>(
  'counter/fetchCounter',
  async (counterId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      // ✅ FIX : ${counterId} au lieu de 'counterId'
      const response = await axios.get(`${baseUrlApi}/counterstatments/${counterId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchCounters());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch counter');
    }
  }
);

export const createCounter = createAsyncThunk<Counter, Partial<Counter>, { state: RootState }>(
  'counter/createCounter',
  async (counterData, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.post(`${baseUrlApi}/counterstatments/`, counterData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchCounters());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create counter');
    }
  }
);

export const updateCounter = createAsyncThunk<
  Counter,
  { counterData: Partial<Counter>; counterId: number },
  { state: RootState }
>(
  'counter/updateCounter',
  async ({ counterData, counterId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      // ✅ FIX : ${counterId} au lieu de 'counterId'
      const response = await axios.put(`${baseUrlApi}/counterstatments/${counterId}`, counterData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchCounters());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update counter');
    }
  }
);

export const deleteCounter = createAsyncThunk<number, number, { state: RootState }>(
  'counter/deleteCounter',
  async (counterId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      // ✅ FIX : ${counterId} au lieu de 'counterId'
      await axios.delete(`${baseUrlApi}/counterstatments/${counterId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchCounters());
      return counterId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete counter');
    }
  }
);

const counterSlice = createSlice({
  name: 'counter',
  initialState,
  reducers: {
    clearCounter: (state) => { state.counter = null; },
    clearCounters: (state) => { state.counters = []; },
    resetCounterState: () => initialState,
  },
  extraReducers: (builder) => {
    [fetchCounters, fetchCounter, createCounter, updateCounter, deleteCounter].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        });
    });

    builder
      .addCase(fetchCounters.fulfilled, (state, action) => {
        state.loading = false;
        state.counters = action.payload;
      })
      .addCase(fetchCounter.fulfilled, (state, action) => {
        state.loading = false;
        state.counter = action.payload;
      })
      .addCase(createCounter.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateCounter.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteCounter.fulfilled, (state, action) => {
        state.loading = false;
        state.counters = state.counters.filter(item => item.id !== action.payload);
      });
  },
});

export const { clearCounter, clearCounters, resetCounterState } = counterSlice.actions;
export default counterSlice.reducer;
